import { MAP_CALIBRATION_STORAGE_KEY } from '../config/constants';
import axiosInstance from './router/axiosInstance';

// WEB-4: серверное хранение калибровки (FloorsConfig.calibration, схема
// viewport-v1) за build-флагом. Дефолт off = легаси-поведение на localStorage
// байт-в-байт. Флаг читается на module-level (конвенция REACT_APP_PLAN_EDITOR).
const CALIBRATION_API_ENABLED = process.env.REACT_APP_CALIBRATION_API === 'true';
const CALIBRATION_SCHEMA = 'viewport-v1';

const DEFAULT_MAP_CALIBRATION = {
  planWidth: 0,
  planHeight: 0,
  offsetLeftPercent: 0,
  offsetTopPercent: 0,
  activeWidthPercent: 100,
  activeHeightPercent: 100,
  invertY: false,
};

const toPositiveNumber = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return 0;
  }

  return numericValue;
};

const clampPercent = (value, fallback, min = 0) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return fallback;
  }

  return Math.min(Math.max(numericValue, min), 100);
};

const getStorage = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  return window.localStorage;
};

const readCalibrationMap = () => {
  const storage = getStorage();

  if (!storage) {
    return {};
  }

  try {
    const rawValue = storage.getItem(MAP_CALIBRATION_STORAGE_KEY);

    if (!rawValue) {
      return {};
    }

    const parsedValue = JSON.parse(rawValue);

    return parsedValue && typeof parsedValue === 'object' ? parsedValue : {};
  } catch (error) {
    return {};
  }
};

const writeCalibrationMap = (value) => {
  const storage = getStorage();

  if (!storage) {
    return;
  }

  storage.setItem(MAP_CALIBRATION_STORAGE_KEY, JSON.stringify(value));
};

const dispatchCalibrationUpdate = (buildingId, floor) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent('map-calibration-updated', {
      detail: {
        buildingId: String(buildingId),
        floor: Number(floor),
      },
    }),
  );
};

const buildCalibrationKey = (buildingId, floor) => `${buildingId}:${floor}`;

const normalizeCalibration = (calibration, fallbackPlanWidth, fallbackPlanHeight) => ({
  planWidth: toPositiveNumber(calibration?.planWidth) || toPositiveNumber(fallbackPlanWidth),
  planHeight: toPositiveNumber(calibration?.planHeight) || toPositiveNumber(fallbackPlanHeight),
  offsetLeftPercent: clampPercent(calibration?.offsetLeftPercent, DEFAULT_MAP_CALIBRATION.offsetLeftPercent),
  offsetTopPercent: clampPercent(calibration?.offsetTopPercent, DEFAULT_MAP_CALIBRATION.offsetTopPercent),
  activeWidthPercent: clampPercent(calibration?.activeWidthPercent, DEFAULT_MAP_CALIBRATION.activeWidthPercent, 1),
  activeHeightPercent: clampPercent(calibration?.activeHeightPercent, DEFAULT_MAP_CALIBRATION.activeHeightPercent, 1),
  invertY: Boolean(calibration?.invertY),
});

// ----- API-режим (за флагом): кэш + фоновая загрузка -----------------------
// Синхронный getMapCalibration сохранён (его зовут MapComponent/PlanEditor из
// рендера): чтение идёт из module-level кэша, промах кэша запускает фоновый
// GET, по ответу диспатчится штатное событие map-calibration-updated — все
// потребители уже слушают его и перечитывают сами, их код не меняется.
// Ключ в кэше ЕСТЬ (значение может быть null) == «сервер УСПЕШНО ответил».
// Сбой загрузки кэш НЕ заполняет (ревью Codex, High: иначе временная сетевая
// ошибка навсегда выключала бы сервер из сессии, а сохранение поверх затирало
// бы реальную серверную калибровку fallback-значением) — неудача пишется в
// отдельную карту с ретрай-окном, чтобы рендер-цикл не долбил лежащий API.
const apiCalibrationCache = new Map();
const pendingCalibrationLoads = new Map();
const failedCalibrationLoads = new Map();
const FAILED_LOAD_RETRY_MS = 30000;

const buildCalibrationEndpoint = (buildingId, floor) =>
  `website/floors/${buildingId}/${floor}/calibration`;

const deleteLegacyCalibrationEntry = (key) => {
  const calibrationMap = readCalibrationMap();

  if (!(key in calibrationMap)) {
    return;
  }

  delete calibrationMap[key];
  writeCalibrationMap(calibrationMap);
};

const toApiPayload = (normalizedCalibration) => ({
  calibration: { schema: CALIBRATION_SCHEMA, ...normalizedCalibration },
});

const fetchCalibrationFromApi = async (buildingId, floor) => {
  const key = buildCalibrationKey(buildingId, floor);
  const response = await axiosInstance.get(buildCalibrationEndpoint(buildingId, floor));
  const serverCalibration = response.data?.calibration ?? null;

  if (serverCalibration) {
    apiCalibrationCache.set(key, serverCalibration);
    // Сервер стал источником истины — легаси-копия ЭТОГО браузера больше
    // никогда не должна импортироваться (ревью-воркфлоу, Medium: иначе браузер
    // со старой записью, зашедший после «Сбросить» в другом браузере, молча
    // воскресил бы сброшенную калибровку своим авто-импортом).
    deleteLegacyCalibrationEntry(key);
    return;
  }

  // Сервер пуст — одноразовый импорт легаси-значения из localStorage: PUT на
  // сервер и только ПОСЛЕ успеха удаляем локальную запись (иначе значение
  // потеряется при сбое). Импорт не срабатывает при недоступном API — ветка
  // достижима только после успешного GET (старый бэкенд без ручки даёт 404
  // и уходит в fail-open ниже, PUT-спама по несуществующей ручке нет).
  const legacyCalibration = readCalibrationMap()[key];

  if (legacyCalibration) {
    const normalizedLegacy = normalizeCalibration(legacyCalibration, 0, 0);
    const putResponse = await axiosInstance.put(
      buildCalibrationEndpoint(buildingId, floor),
      toApiPayload(normalizedLegacy),
    );

    apiCalibrationCache.set(key, putResponse.data?.calibration ?? normalizedLegacy);
    deleteLegacyCalibrationEntry(key);
    return;
  }

  apiCalibrationCache.set(key, null);
};

const ensureCalibrationLoaded = (buildingId, floor, { force = false } = {}) => {
  if (!CALIBRATION_API_ENABLED || !buildingId || !floor) {
    return Promise.resolve();
  }

  const key = buildCalibrationKey(buildingId, floor);

  // force обходит и кэш-хит (ревью-воркфлоу, Low: иначе панель после первого
  // успеха никогда не перечитывала сервер — правки с другой машины невидимы
  // до перезагрузки вкладки), и окно ретрая после сбоя ниже.
  if (!force && apiCalibrationCache.has(key)) {
    return Promise.resolve();
  }

  if (pendingCalibrationLoads.has(key)) {
    return pendingCalibrationLoads.get(key);
  }

  const failedAt = failedCalibrationLoads.get(key);

  if (!force && failedAt !== undefined && Date.now() - failedAt < FAILED_LOAD_RETRY_MS) {
    // Недавняя неудача: фоновые вызовы из рендера ждут окно ретрая, живя на
    // fail-open-значении из getMapCalibration; явная загрузка (force) ретраит.
    return Promise.resolve();
  }

  const loadPromise = fetchCalibrationFromApi(buildingId, floor)
    .then(() => {
      failedCalibrationLoads.delete(key);
    })
    .catch(() => {
      // Fail-open: API недоступен/бэкенд без ручки — кэш НЕ заполняем (сервер
      // не отвечал), клиент живёт на легаси-значении localStorage (запись не
      // удаляем, импорт попробует следующий ретрай).
      failedCalibrationLoads.set(key, Date.now());
    })
    .then(() => {
      pendingCalibrationLoads.delete(key);
      dispatchCalibrationUpdate(buildingId, floor);
    });

  pendingCalibrationLoads.set(key, loadPromise);

  return loadPromise;
};

// ---------------------------------------------------------------------------

export const getMapCalibration = ({ buildingId, floor, fallbackPlanWidth = 0, fallbackPlanHeight = 0 }) => {
  if (!buildingId || !floor) {
    return normalizeCalibration(DEFAULT_MAP_CALIBRATION, fallbackPlanWidth, fallbackPlanHeight);
  }

  if (CALIBRATION_API_ENABLED) {
    const key = buildCalibrationKey(buildingId, floor);

    if (!apiCalibrationCache.has(key)) {
      ensureCalibrationLoaded(buildingId, floor);

      // До ответа сервера — оптимистично легаси-значение (у пользователей с
      // локальной калибровкой карта не мигает дефолтом на время загрузки).
      return normalizeCalibration(readCalibrationMap()[key], fallbackPlanWidth, fallbackPlanHeight);
    }

    return normalizeCalibration(apiCalibrationCache.get(key), fallbackPlanWidth, fallbackPlanHeight);
  }

  const calibrationMap = readCalibrationMap();
  const storedCalibration = calibrationMap[buildCalibrationKey(buildingId, floor)];

  return normalizeCalibration(storedCalibration, fallbackPlanWidth, fallbackPlanHeight);
};

// «Сервер уже успешно отвечал за этот этаж?» — панель калибровки обязана
// проверять это перед сохранением (ревью Codex ×2, High): PUT поверх
// НЕзагруженной калибровки затёр бы серверное значение fallback-формой при
// временно упавшем GET (но живом PUT). При выключенном флаге истина всегда —
// localStorage синхронен по определению.
export const isMapCalibrationLoaded = ({ buildingId, floor }) => {
  if (!CALIBRATION_API_ENABLED) {
    return true;
  }

  if (!buildingId || !floor) {
    return false;
  }

  return apiCalibrationCache.has(buildCalibrationKey(buildingId, floor));
};

// Явная (awaitable) загрузка для панели калибровки: форма должна заполниться
// уже загруженным значением, а не гадать по кэшу. При выключенном флаге
// резолвится сразу легаси-значением — вызывающему коду ветвиться не нужно.
export const loadMapCalibration = async ({ buildingId, floor, fallbackPlanWidth = 0, fallbackPlanHeight = 0 }) => {
  // force: открытие панели/явный запрос ретраит и внутри окна после неудачи.
  await ensureCalibrationLoaded(buildingId, floor, { force: true });

  return getMapCalibration({ buildingId, floor, fallbackPlanWidth, fallbackPlanHeight });
};

export const saveMapCalibration = ({
  buildingId,
  floor,
  calibration,
  fallbackPlanWidth = 0,
  fallbackPlanHeight = 0,
}) => {
  if (!buildingId || !floor) {
    return normalizeCalibration(calibration, fallbackPlanWidth, fallbackPlanHeight);
  }

  const normalizedCalibration = normalizeCalibration(calibration, fallbackPlanWidth, fallbackPlanHeight);
  const key = buildCalibrationKey(buildingId, floor);

  if (CALIBRATION_API_ENABLED) {
    // API-режим возвращает Promise (панель показывает ошибку сохранения);
    // легаси-режим ниже — синхронное значение. Вызывающие оборачивают в
    // Promise.resolve(...) и работают одинаково с обоими.
    return axiosInstance
      .put(buildCalibrationEndpoint(buildingId, floor), toApiPayload(normalizedCalibration))
      .then((response) => {
        apiCalibrationCache.set(key, response.data?.calibration ?? normalizedCalibration);
        deleteLegacyCalibrationEntry(key);
        dispatchCalibrationUpdate(buildingId, floor);

        return normalizeCalibration(apiCalibrationCache.get(key), fallbackPlanWidth, fallbackPlanHeight);
      });
  }

  const calibrationMap = readCalibrationMap();

  calibrationMap[key] = normalizedCalibration;
  writeCalibrationMap(calibrationMap);
  dispatchCalibrationUpdate(buildingId, floor);

  return normalizedCalibration;
};

export const clearMapCalibration = ({ buildingId, floor }) => {
  if (!buildingId || !floor) {
    return undefined;
  }

  const key = buildCalibrationKey(buildingId, floor);

  if (CALIBRATION_API_ENABLED) {
    return axiosInstance
      .put(buildCalibrationEndpoint(buildingId, floor), { calibration: null })
      .then(() => {
        apiCalibrationCache.set(key, null);
        // Легаси-запись тоже подчищаем: иначе следующий импорт (сервер пуст +
        // запись в localStorage) воскресил бы только что сброшенную калибровку.
        deleteLegacyCalibrationEntry(key);
        dispatchCalibrationUpdate(buildingId, floor);
      });
  }

  const calibrationMap = readCalibrationMap();

  delete calibrationMap[key];

  writeCalibrationMap(calibrationMap);
  dispatchCalibrationUpdate(buildingId, floor);

  return undefined;
};
